import { addDays, dayLabel, requestDate, requestWindowLabel } from '@kronos/domain'
import type { LocalDate } from '@kronos/domain'
import type { ComeInRequestView } from '../../data/schedule'
import { Badge } from '../primitives'
import { DeclineDialog, useAnswer } from './AnswerRow'
import { relativeDay } from './shared'

/**
 * A shift the technician accepted, with a way out — in red, because it takes
 * back a promise somebody is counting on.
 *
 * "Can't make it" declines it until the start time (`answerTransition`), with
 * an optional reason, and puts it in front of the engineer who asked. Plans
 * change; the alternative is the phone call this whole feature replaces. Once
 * the shift has started the button is gone — that is a conversation.
 */
export function BookedRow({
  request,
  timeZone,
  today,
  now,
}: {
  request: ComeInRequestView
  timeZone: string
  today: LocalDate
  now: string
}): JSX.Element {
  const { answer, declining, setDeclining, problem } = useAnswer(request.id)
  const date = requestDate(request, timeZone)
  const when = relativeDay(date, today, addDays(today, 1))
  const started = Date.parse(request.startsAt) <= Date.parse(now)

  return (
    <div className="row-item">
      <div className="row-item__main">
        <span className="row-item__title">
          {dayLabel(date)} · <span className="figure">{requestWindowLabel(request, timeZone)}</span>
        </span>
        <span className="row-item__meta">
          {request.note || 'No note'} · asked by {request.requestedByName}
        </span>
        {problem ? (
          <span className="critical" role="alert">
            {problem}
          </span>
        ) : null}
      </div>
      <div className="row-item__side">
        {when ? <Badge tone="healthy">{when}</Badge> : null}
        {started ? null : (
          <button className="danger booked-out" disabled={answer.isPending} onClick={() => setDeclining(true)}>
            Can’t make it
          </button>
        )}
      </div>
      {declining ? (
        <DeclineDialog
          heading="Can’t make it?"
          confirm="Let them know"
          busy={answer.isPending}
          title={`${dayLabel(date)}, ${requestWindowLabel(request, timeZone)} — it shows on ${request.requestedByName}’s schedule.`}
          onClose={() => setDeclining(false)}
          onDecline={(reply) => answer.mutate({ answer: 'decline', reply })}
        />
      ) : null}
    </div>
  )
}
